import React from 'react';
import type { AddUserFormData, CustomerLite, FormErrors } from './addUserTypes';

type Props = {
    formData: AddUserFormData;
    errors: FormErrors;
    customers: CustomerLite[];
    isEditMode: boolean;
    showPassword: boolean;
    showEncryptedPassword: boolean;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onUserTypeChange: (userType: 'Internal' | 'External') => void;
    onSsoEnabledChange: (enabled: boolean) => void;
    onToggleShowPassword: () => void;
};

const PasswordVisibilityIcon: React.FC<{ hidden: boolean }> = ({ hidden }) => (
    hidden ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12C2 12 5 5 12 5s10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12C2 12 5 5 12 5s10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
);

const AddUserUserInfoSection: React.FC<Props> = ({
    formData,
    errors,
    customers,
    isEditMode,
    showPassword,
    showEncryptedPassword,
    onInputChange,
    onUserTypeChange,
    onSsoEnabledChange,
    onToggleShowPassword,
}) => (
    <div className="form-section">
        <h3 className="section-header">User Information</h3>

        <div className="form-group">
            <label>Select User Type</label>
            <div className="user-type-options">
                <button
                    type="button"
                    className={`user-type-button ${formData.userType === 'Internal' ? 'active' : ''}`}
                    onClick={() => onUserTypeChange('Internal')}
                >
                    Internal
                </button>
                <button
                    type="button"
                    className={`user-type-button ${formData.userType === 'External' ? 'active' : ''}`}
                    onClick={() => onUserTypeChange('External')}
                >
                    External
                </button>
            </div>
            {formData.userType === 'External' && (
                <div className="form-group">
                    <label>Customer</label>
                    <select
                        name="customerId"
                        value={formData.customerId || ''}
                        onChange={onInputChange}
                        required
                    >
                        <option value="">-- Select Customer --</option>
                        {customers.map((customer) => (
                            <option key={customer.customerId} value={customer.customerId}>
                                {customer.customerName}{customer.soldTo ? ` (#${customer.soldTo})` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {formData.userType === 'External' && errors.customerId && (
                <div className="error-message" style={{ color: 'red' }}>
                    {errors.customerId}
                </div>
            )}
        </div>

        <div className="form-group">
            <div className="sso-toggle-container">
                <span>SSO Enabled</span>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={formData.ssoEnabled}
                        onChange={(e) => onSsoEnabledChange(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                </label>
            </div>
            <label>Email Address <span className='required'>*</span></label>
            <div className="email-container">
                <div className="email-input">
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={onInputChange}
                        placeholder="Enter email address"
                        className={errors.email ? 'error-input' : ''}
                    />
                    {errors.email && <span className="error-message-email">{errors.email}</span>}
                </div>
                {!formData.ssoEnabled && (
                    <div className="password-input">
                        <label>Password<span className='required'>*</span></label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={showEncryptedPassword && !showPassword ? '••••••••••••' : formData.password}
                                onChange={onInputChange}
                                placeholder={isEditMode ? '' : 'Enter password'}
                                className={errors.password ? 'error-input' : ''}
                            />
                            {!isEditMode && (
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={onToggleShowPassword}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#666',
                                    }}
                                >
                                    <PasswordVisibilityIcon hidden={showPassword} />
                                </button>
                            )}
                        </div>
                        {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>
                )}
            </div>
        </div>

        <div className="form-group">
            <label>First Name<span className='required'>*</span></label>
            <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={onInputChange}
                placeholder="Enter first name"
                className={errors.firstName ? 'error-input' : ''}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>

        <div className="form-group">
            <label>Last Name<span className='required'>*</span></label>
            <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={onInputChange}
                placeholder="Enter last name"
                className={errors.lastName ? 'error-input' : ''}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>

        <div className="form-group phone-form-group">
            <label>Mobile Phone Number</label>
            <div className="phone-input-group">
                <input
                    type="text"
                    className="phone-code"
                    name="mobilePhoneCode"
                    value={formData.mobilePhoneCode}
                    onChange={onInputChange}
                    placeholder="+1"
                    maxLength={3}
                />
                <input
                    type="tel"
                    className="phone-number"
                    name="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={onInputChange}
                    placeholder="(123) 456-7890"
                />
            </div>
        </div>

        <div className="form-group phone-form-group">
            <label>Office Phone Number</label>
            <div className="phone-input-group">
                <input
                    type="text"
                    className="phone-code"
                    name="officePhoneCode"
                    value={formData.officePhoneCode}
                    onChange={onInputChange}
                    placeholder="+1"
                    maxLength={3}
                />
                <input
                    type="tel"
                    className="phone-number"
                    name="officePhone"
                    value={formData.officePhone}
                    onChange={onInputChange}
                    placeholder="(123) 456-7890"
                />
            </div>
        </div>

        <div className="form-row">
            <div className="form-group">
                <label>Access Level</label>
                <select name="role" value={formData.role} onChange={onInputChange} required>
                    {formData.userType === 'Internal' ? (
                        <>
                            <option value="Admin">Admin</option>
                            <option value="User">User</option>
                        </>
                    ) : (
                        <option value="User">User</option>
                    )}
                </select>
            </div>
        </div>

        <div className="form-row">
            <div className="form-group">
                <label className='user-status'>Status</label>
                <select name="status" value={formData.status} onChange={onInputChange}>
                    <option value="Enabled">Enabled</option>
                    <option value="Disabled">Disabled</option>
                </select>
            </div>
        </div>
    </div>
);

export default AddUserUserInfoSection;
